<template>
  <section id="cancel" class="box_sso cancel">
    <SSOBackground />
    <div class="container">
      <div class="box_table">
        <div class="cell">
          <a-row>
            <a-col :xs="24">
              <a-form ref="form"
                      :model="form.data" :rules="form.rule">
                <a-typography-title :level="1">
                  账号注销
                </a-typography-title>
                <a-alert type="warning" show-icon>
                  <template #message>
                    注销申请提交后，账号会进入注销缓冲期
                  </template>
                  <template #description>
                    缓冲期内账号无法登录，手机号和邮箱不能绑定到其他账号；缓冲期结束后，账号及相关数据将无法找回。若误操作，可在登录时按提示终止注销。
                  </template>
                </a-alert>
                <a-form-item name="uid" has-feedback>
                  <a-input v-model:value="form.data.uid" placeholder="用户ID"
                           :maxlength="$Check.Constraint.Max2X"
                           :show-count="form.data.uid.length>0" disabled>
                    <template #prefix>
                      <IdcardOutlined class="input_icon" />
                    </template>
                  </a-input>
                </a-form-item>
                <a-form-item name="phone" has-feedback>
                  <a-input v-model:value="form.data.phone" placeholder="手机号"
                           :maxlength="$Check.Constraint.Phone"
                           :show-count="form.data.phone.length>0" allow-clear>
                    <template #prefix>
                      <MobileOutlined class="input_icon" />
                    </template>
                  </a-input>
                </a-form-item>
                <a-row class="box_auth">
                  <a-col :span="13">
                    <a-form-item name="code" has-feedback>
                      <a-input v-model:value="form.data.code" placeholder="验证码"
                               :maxlength="$Check.Constraint.AuthX"
                               :show-count="form.data.code.length>0" allow-clear>
                        <template #prefix>
                          <SafetyCertificateOutlined class="input_icon" />
                        </template>
                      </a-input>
                    </a-form-item>
                  </a-col>
                  <a-col :offset="1" :span="10">
                    <a-button type="primary" danger
                              :loading="request.intercept.auth"
                              :disabled="setTime.countdown"
                              @click="getAuthCode" block>
                      {{ expand.countdown.text }}
                    </a-button>
                  </a-col>
                </a-row>
                <a-form-item>
                  <a-checkbox v-model:checked="expand.protocol">
                    我已阅读并同意账号注销协议，理解注销后账号、企业、应用、表单、附件、第三方绑定等信息可能无法恢复。
                  </a-checkbox>
                </a-form-item>
                <a-row>
                  <a-col :span="24">
                    <a-button type="primary" danger
                              :loading="request.intercept.submit"
                              @click="submit" block>
                      确认注销
                    </a-button>
                  </a-col>
                  <a-col :span="24">
                    <a-button type="link" class="button_register"
                              @click="back" block>
                      暂不注销
                    </a-button>
                  </a-col>
                </a-row>
              </a-form>
            </a-col>
          </a-row>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" type="text/tsx">
import {
  IdcardOutlined,
  MobileOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons-vue';

import { DataInterface } from '@interface/public';
import {
  AuthTypeEnum, AuthUseEnum,
  RequestAuth, ResponseAuth, AxiosAuth,
  RequestCancelUser, ResponseCancelUser, AxiosCancelUser
} from '@server/apiSSO';
import SSOBackground from '@template/sso/_component/background.vue';

export default {
  name: 'cancel',
  components: {
    IdcardOutlined,
    MobileOutlined,
    SafetyCertificateOutlined,
    SSOBackground
  },
  data(): DataInterface {
    return {
      setTime: {
        countdown: null
      },
      request: {
        intercept: {
          auth: false,
          submit: false
        }
      },
      form: {
        rule: {
          uid: [ $Check.Rule.Required('用户ID') ],
          phone: $Check.Rule.Phone(),
          code: $Check.Rule.Auth('')
        },
        data: {
          uid: $Store.User().userID || this.$route.query.uid || '',
          phone: '',
          code: ''
        }
      },
      expand: {
        countdown: {
          current: 0,
          total: 60,
          text: '获取验证码'
        },
        protocol: false
      }
    };
  },
  mounted(): void {
    if (!$RQ.SSO.check()) {
      $RQ.SSO.login(location.href, $Enum.OperateEnum.Cancel);
      return;
    }
    
    this.form.data.uid = $Store.User().userID || this.form.data.uid;
  },
  methods: {
    /**
     * 倒计时
     */
    countdown(): void {
      this.expand.countdown.current = this.expand.countdown.total;
      this.expand.countdown.text = `${ this.expand.countdown.current }秒后重新获取`;
      
      this.setTime.countdown && clearInterval(this.setTime.countdown);
      this.setTime.countdown = setInterval(() => {
        if (this.expand.countdown.current <= 0) {
          clearInterval(this.setTime.countdown);
          this.setTime.countdown = null;
          this.expand.countdown.text = '获取验证码';
          return;
        }
        
        this.expand.countdown.current--;
        this.expand.countdown.text = `${ this.expand.countdown.current }秒后重新获取`;
      }, 1000);
    },
    
    /**
     * 获取注销验证码
     */
    async getAuthCode(): Promise<any> {
      await this.$refs.form.validateFields([ 'phone' ])
        .then(async () => {
          if (this.request.intercept.auth || this.expand.countdown.current !== 0) return;
          this.request.intercept.auth = true;
          
          await AxiosAuth({
            usage: AuthUseEnum.Cancel,
            target_type: AuthTypeEnum.Phone,
            target: this.form.data.phone
          } as RequestAuth)
            .then((response: any) => {
              const data: ResponseAuth = response.data;
              
              if (data.code !== $Enum.CodeEnum.Normal) return;
              
              this.countdown();
              $FN.Message('success', {
                content: $Enum.TextEnum.SuccessGet
              });
            })
            .finally(() => {
              this.request.intercept.auth = false;
            });
        })
        .catch(() => {
          $FN.Message('warning', {
            content: $Enum.TextEnum.ErrorCheckFail
          });
        });
    },
    
    /**
     * 提交注销
     */
    async submit(): Promise<any> {
      await this.$refs.form.validateFields()
        .then(async () => {
          if (!this.expand.protocol) {
            $FN.Message('warning', {
              content: '请先阅读并同意账号注销协议'
            });
            return;
          }
          if (this.request.intercept.submit) return;
          this.request.intercept.submit = true;
          
          await AxiosCancelUser({
            uid: this.form.data.uid,
            phone: this.form.data.phone,
            code: Number(this.form.data.code)
          } as RequestCancelUser)
            .then((response: any) => {
              const data: ResponseCancelUser = response.data;
              
              if (data.code !== $Enum.CodeEnum.Normal) return;
              
              $FN.Message('success', {
                content: '注销申请已提交'
              });
              $RQ.SSO.logout();
              this.$router.push($Enum.RouterEnum.SSOIndex);
            })
            .finally(() => {
              this.request.intercept.submit = false;
            });
        })
        .catch(() => {
          $FN.Message('warning', {
            content: $Enum.TextEnum.ErrorCheckFail
          });
        });
    },
    
    /**
     * 返回
     */
    back(): void {
      this.$router.push($Enum.RouterEnum.Back);
    }
  }
};
</script>

<style lang="less" rel="stylesheet/less" scoped>
@import '@resource/css/_global.less';
@import '@resource/css/sso.less';

@image: '@{src_image}sso/';

.cancel {
  .ant-form {
    background: fade(@color_white, 95);
    .shadow(0 10px 30px fade(@color_black, 10));
    
    h1.ant-typography {
      margin: 0 0 20px;
    }
    
    .ant-alert {
      margin: 0 0 20px;
      text-align: left;
    }
  }
}
</style>
